import { useEffect, useState } from "react";
import { appConfirm } from "@/components/appConfirm";
import { toast } from "sonner";
import { X, ChevronLeft, ChevronRight, AlertTriangle, Download, CalendarOff, Ban, Lock, FileSignature } from "lucide-react";
import { BookingWorkflowModal } from "@/components/BookingWorkflowModal";
import { ContractSettingsModal } from "@/components/ContractSettingsModal";
import { BlocksPanel } from "@/components/BlocksPanel";
import {
  adminBookingsDashboard,
  adminBookingsCalendar,
  adminUpdateBooking,
  adminDeleteBooking,
  adminExportBookingsCsvUrl,
  adminCreateDepositLink,
  adminListAvailabilityBlocks,
  adminAddAvailabilityBlock,
  adminRemoveAvailabilityBlock,
  adminAddBulkBlocks,
  adminBulkUnblock,
  availabilityResources,
  extractApiError,
} from "@/lib/api";

export const SERVICE_META = {
  social_bar: { label: "The Social Bar",       short: "BAR",      color: "#A01E2A" },
  social_set: { label: "The Social Set",       short: "SET",      color: "#B8962B" },
  luxe_dome:  { label: "Luxe Dome",            short: "DOME",     color: "#6A4CBE" },
  lit_lounge: { label: "Lit Lounge",           short: "LOUNGE",   color: "#2E7D5B" },
  nightclub:  { label: "Nightclub (legacy)",   short: "NIGHTCLUB", color: "#6A4CBE" },
  supply:     { label: "The Social Supply",    short: "SUPPLY",   color: "#8A6B3B" },
};

const STATUS_FLOW = ["enquiry", "tentative", "confirmed", "paid_in_full", "completed", "cancelled"];
export const STATUS_META = {
  enquiry:   { label: "Pending Review",     color: "var(--ss-cream-dim)", bg: "transparent" },
  tentative: { label: "Tentative / Hold",   color: "#0F0E0C",            bg: "#D9CFBE" },
  quoted:    { label: "Quoted",             color: "#0F0E0C",            bg: "#B8962B" },
  approved:  { label: "Approved · Awaiting Signature", color: "#0F0E0C", bg: "#B8962B" },
  contract_signed: { label: "Signed · Awaiting Retainer", color: "#F5F1E8", bg: "#A01E2A" },
  deposit:   { label: "Retainer received",  color: "#F5F1E8",            bg: "#A01E2A" },
  confirmed: { label: "Confirmed",          color: "#F5F1E8",            bg: "#2E7D5B" },
  paid_in_full: { label: "Paid in Full",    color: "#F5F1E8",            bg: "#1F5A40" },
  declined:  { label: "Declined",           color: "#F5F1E8",            bg: "#6B2E2E" },
  completed: { label: "Completed",          color: "#F5F1E8",            bg: "#4a463f" },
  cancelled: { label: "Cancelled",          color: "#F5F1E8",            bg: "#6B2E2E" },
  done:      { label: "Completed",          color: "#F5F1E8",            bg: "#4a463f" }, // legacy
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function Badge({ status, testId }) {
  const m = STATUS_META[status] || STATUS_META.enquiry;
  return (
    <span data-testid={testId}
          className="font-label text-[0.6rem] tracking-widest px-2.5 py-1 border inline-block"
          style={{ background: m.bg, color: m.color, borderColor: m.bg === "transparent" ? "var(--ss-border)" : m.bg }}>
      {m.label}
    </span>
  );
}

function ServicePill({ serviceKey }) {
  const m = SERVICE_META[serviceKey];
  if (!m) return null;
  return (
    <span className="font-label text-[0.6rem] tracking-widest px-2 py-1 inline-flex items-center gap-1.5"
          style={{ background: `${m.color}22`, color: m.color, border: `1px solid ${m.color}` }}>
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
      {m.short}
    </span>
  );
}

function DashCard({ label, count, items, tone, testId }) {
  const toneColor = tone === "rust" ? "var(--ss-rust)" : tone === "gold" ? "var(--ss-gold)" : "var(--ss-cream-dim)";
  return (
    <div data-testid={testId}
         className="p-5 border" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }}>
      <div className="flex items-baseline justify-between">
        <div className="font-label text-[0.65rem] tracking-widest" style={{ color: toneColor }}>{label}</div>
        <div className="font-display text-4xl" style={{ color: "var(--ss-cream)" }}>{count}</div>
      </div>
      <div className="mt-3 space-y-1">
        {(items || []).slice(0, 3).map((b, i) => (
          <div key={b.id || i} className="text-xs truncate" style={{ color: "var(--ss-cream-dim)" }}>
            {b.event_date} · {b.name} · {(b.services || []).map(s => SERVICE_META[s]?.short).filter(Boolean).join(" + ") || "—"}
          </div>
        ))}
        {(!items || items.length === 0) && (
          <div className="text-xs italic" style={{ color: "var(--ss-muted)" }}>None yet</div>
        )}
      </div>
    </div>
  );
}

export default function BookingsTracker({ bookings, token, giftCards, onReload, onOpenGiftRedeem }) {
  const [view, setView] = useState("list");
  const [dash, setDash] = useState(null);
  const [cal, setCal] = useState(null);
  const [calLoading, setCalLoading] = useState(false);
  const now = new Date();
  const [calMonth, setCalMonth] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [notesDraft, setNotesDraft] = useState({});
  const [dayDetail, setDayDetail] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [blocksOpen, setBlocksOpen] = useState(false);
  const [resources, setResources] = useState([]);
  const [newBlock, setNewBlock] = useState({ resource: "social_bar", date: "", reason: "" });
  const [depositModal, setDepositModal] = useState(null); // {booking, amount, description, link, loading}
  const [manageId, setManageId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const loadDash = async () => {
    try { setDash(await adminBookingsDashboard(token)); } catch (_) {}
  };
  const loadCal = async () => {
    setCalLoading(true);
    try { setCal(await adminBookingsCalendar(token, calMonth.y, calMonth.m)); }
    catch (e) { toast.error(extractApiError(e, "Could not load calendar")); }
    finally { setCalLoading(false); }
  };
  const loadBlocks = async () => {
    try { setBlocks(await adminListAvailabilityBlocks(token)); } catch (_) {}
  };
  useEffect(() => { if (token) { loadDash(); loadBlocks(); availabilityResources().then(r => setResources(r.resources)).catch(() => {}); } /* eslint-disable-next-line */ }, [token, bookings.length]);
  useEffect(() => { if (token && view === "calendar") loadCal(); /* eslint-disable-next-line */ }, [token, view, calMonth.y, calMonth.m]);

  const changeStatus = async (b, status) => {
    try {
      await adminUpdateBooking(token, b.id, { status });
      toast.success(`${b.name}: ${STATUS_META[status].label}`);
      await onReload(); loadDash();
      if (view === "calendar") loadCal();
    } catch (err) { toast.error(extractApiError(err, "Could not update status")); }
  };
  const saveNotes = async (b) => {
    const notes = notesDraft[b.id] ?? b.tracker_notes ?? "";
    try {
      await adminUpdateBooking(token, b.id, { tracker_notes: notes });
      toast.success("Notes saved");
      await onReload();
      setNotesDraft((p) => { const q = { ...p }; delete q[b.id]; return q; });
    } catch (err) { toast.error(extractApiError(err, "Could not save notes")); }
  };
  const removeBooking = async (b) => {
    if (!(await appConfirm(`Delete booking from ${b.name}? This cannot be undone.`))) return;
    try {
      await adminDeleteBooking(token, b.id);
      toast.success("Booking deleted");
      await onReload(); loadDash();
      if (view === "calendar") loadCal();
    } catch (err) { toast.error(extractApiError(err, "Delete failed")); }
  };

  const addBlock = async () => {
    if (!newBlock.date || !newBlock.resource) { toast.error("Pick a resource and date"); return; }
    try {
      await adminAddAvailabilityBlock(token, newBlock);
      toast.success(`Blocked ${newBlock.date} for ${newBlock.resource}`);
      setNewBlock({ resource: newBlock.resource, date: "", reason: "" });
      loadBlocks();
    } catch (e) { toast.error(extractApiError(e, "Could not add block")); }
  };
  const removeBlock = async (id) => {
    if (!(await appConfirm("Unblock this date?"))) return;
    try { await adminRemoveAvailabilityBlock(token, id); toast.success("Unblocked"); loadBlocks(); }
    catch (e) { toast.error(extractApiError(e, "Could not unblock")); }
  };

  const openDeposit = (b) => setDepositModal({ booking: b, amount: "", description: "", link: null, loading: false });
  const generateDepositLink = async () => {
    if (!depositModal) return;
    const amt = Number(depositModal.amount);
    if (!amt || amt < 1) { toast.error("Enter a valid deposit amount"); return; }
    setDepositModal((d) => ({ ...d, loading: true }));
    try {
      const r = await adminCreateDepositLink(token, depositModal.booking.id, amt, depositModal.description);
      setDepositModal((d) => ({ ...d, link: r.url, loading: false }));
      toast.success("Deposit link ready — paste to the customer");
    } catch (e) {
      setDepositModal((d) => ({ ...d, loading: false }));
      toast.error(extractApiError(e, "Could not create deposit link"));
    }
  };

  // ---- Day-click block/unblock from admin calendar ----
  const [dayBlockForm, setDayBlockForm] = useState({ resource: "all", end_date: "", reason: "" });

  const openDay = (date) => {
    setDayDetail(date);
    setDayBlockForm({ resource: "all", end_date: "", reason: "" });
  };

  const blockFromDay = async () => {
    if (!dayDetail) return;
    try {
      const payload = {
        resource: dayBlockForm.resource,
        start_date: dayDetail,
        reason: dayBlockForm.reason || "",
      };
      if (dayBlockForm.end_date && dayBlockForm.end_date >= dayDetail) payload.end_date = dayBlockForm.end_date;
      const r = await adminAddBulkBlocks(token, payload);
      toast.success(`${r.created} new · ${r.updated} updated blocks`);
      setDayBlockForm({ resource: dayBlockForm.resource, end_date: "", reason: "" });
      await loadCal(); await loadBlocks();
    } catch (e) { toast.error(extractApiError(e, "Could not block date")); }
  };

  const unblockOne = async (blockId) => {
    if (!(await appConfirm("Unblock this service on this date?"))) return;
    try {
      await adminRemoveAvailabilityBlock(token, blockId);
      toast.success("Unblocked");
      await loadCal(); await loadBlocks();
    } catch (e) { toast.error(extractApiError(e, "Could not unblock")); }
  };

  const unblockAllForDay = async (date) => {
    if (!(await appConfirm(`Unblock every service on ${date}? (Confirmed bookings remain in place.)`))) return;
    try {
      const r = await adminBulkUnblock(token, { resource: "all", start_date: date });
      toast.success(`Unblocked ${r.deleted}`);
      await loadCal(); await loadBlocks();
    } catch (e) { toast.error(extractApiError(e, "Could not unblock")); }
  };

  const prevMonth = () => setCalMonth((m) => (m.m === 1 ? { y: m.y - 1, m: 12 } : { ...m, m: m.m - 1 }));
  const nextMonth = () => setCalMonth((m) => (m.m === 12 ? { y: m.y + 1, m: 1 } : { ...m, m: m.m + 1 }));
  const jumpToday = () => setCalMonth({ y: new Date().getFullYear(), m: new Date().getMonth() + 1 });

  return (
    <div className="mt-8" data-testid="bookings-tracker">
      {manageId && <BookingWorkflowModal bookingId={manageId} token={token} onClose={() => setManageId(null)} onChanged={() => { onReload?.(); loadDash(); }} />}
      {settingsOpen && <ContractSettingsModal token={token} onClose={() => setSettingsOpen(false)} />}
      {blocksOpen && <BlocksPanel blocks={blocks} resources={resources} newBlock={newBlock} setNewBlock={setNewBlock}
                                  onAdd={addBlock} onRemove={removeBlock} onClose={() => setBlocksOpen(false)} />}
      {/* Dashboard */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <DashCard testId="dash-new-enquiries" tone="rust"  label="New Enquiries"    count={dash?.new_enquiries.count ?? 0} items={dash?.new_enquiries.items} />
        <DashCard testId="dash-upcoming"      tone="gold"  label="Upcoming Events"  count={dash?.upcoming.count ?? 0}      items={dash?.upcoming.items} />
        <DashCard testId="dash-to-confirm"    tone="cream" label="Bookings to Confirm" count={dash?.to_confirm.count ?? 0} items={dash?.to_confirm.items} />
      </div>

      {/* Toggle + seed */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="inline-flex border" style={{ borderColor: "var(--ss-rust)" }}>
          <button data-testid="bookings-view-list"
                  onClick={() => setView("list")}
                  className="font-label text-xs tracking-widest px-5 py-2"
                  style={{ background: view === "list" ? "var(--ss-rust)" : "transparent", color: view === "list" ? "var(--ss-bg)" : "var(--ss-cream)" }}>
            List
          </button>
          <button data-testid="bookings-view-calendar"
                  onClick={() => setView("calendar")}
                  className="font-label text-xs tracking-widest px-5 py-2"
                  style={{ background: view === "calendar" ? "var(--ss-rust)" : "transparent", color: view === "calendar" ? "var(--ss-bg)" : "var(--ss-cream)" }}>
            Calendar
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <a data-testid="bookings-export-csv" href={adminExportBookingsCsvUrl()} download className="btn-outline inline-flex items-center gap-2">
            <Download size={14} /> Export CSV
          </a>
          <button data-testid="bookings-blocks-btn" onClick={() => setBlocksOpen(true)} className="btn-outline inline-flex items-center gap-2">
            <CalendarOff size={14} /> Manual Blocks ({blocks.length})
          </button>
          <button data-testid="bookings-contract-settings-btn" onClick={() => setSettingsOpen(true)} className="btn-outline inline-flex items-center gap-2">
            <FileSignature size={14} /> Contract & Retainer
          </button>
        </div>
      </div>

      {view === "list" && (
        <div className="space-y-4" data-testid="bookings-list">
          {bookings.length === 0 && <p style={{ color: "var(--ss-cream-dim)" }}>No booking requests yet.</p>}
          {bookings.map((b) => {
            const linkedCard = b.gift_card_code ? (giftCards || []).find((g) => g.code === b.gift_card_code) : null;
            const cardRedeemed = linkedCard?.redeemed;
            const draft = notesDraft[b.id];
            const notesDirty = draft !== undefined && draft !== (b.tracker_notes || "");
            const services = (b.services || []).filter((s) => SERVICE_META[s]);
            return (
              <div key={b.id} className="p-6 border" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }}
                   data-testid={`booking-row-${b.id}`}>
                <div className="flex flex-wrap justify-between gap-3">
                  <div className="min-w-0">
                    {b.booking_ref && <div className="font-label text-[0.6rem]" style={{ color: "var(--ss-rust)" }} data-testid={`booking-ref-${b.id}`}>{b.booking_ref}</div>}
                    <div className="font-display text-2xl">
                      {b.name}
                      {b.is_bundle && <span className="ml-3 font-label text-[0.65rem]" style={{ color: "var(--ss-gold)" }}>{["executive_luxe", "executive_lit"].includes(b.supply_request?.package) ? "EXECUTIVE" : "BUNDLE"}</span>}
                      {b.is_sample && <span className="ml-3 font-label text-[0.65rem]" style={{ color: "var(--ss-muted)" }}>SAMPLE</span>}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 items-center">
                      <Badge status={b.status} testId={`booking-status-${b.id}`} />
                      {services.map((s) => <ServicePill key={s} serviceKey={s} />)}
                    </div>
                  </div>
                  <div className="font-label text-[0.65rem] text-right" style={{ color: "var(--ss-muted)" }}>
                    Inquired {new Date(b.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="mt-4 grid md:grid-cols-2 gap-2 text-sm" style={{ color: "var(--ss-cream-dim)" }}>
                  <div>{b.email} • {b.phone}</div>
                  <div>{b.event_type} — {b.guest_count} guests</div>
                  <div><span style={{ color: "var(--ss-cream)" }}>{b.event_date}</span> • {b.event_location}</div>
                  <div>Services: {services.length ? services.map((s) => SERVICE_META[s].short).join(" + ") : "—"}</div>
                </div>

                {(b.gift_card_code || b.estimated_total > 0 || b.discount_code) && (
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-sm" style={{ color: "var(--ss-cream-dim)" }}>
                    {b.estimated_total > 0 && (
                      <span className="font-label text-[0.65rem] px-2 py-1 border" style={{ borderColor: "var(--ss-border)" }}>
                        EST. TOTAL <span className="ml-1" style={{ color: "var(--ss-rust)" }}>${Math.floor(b.estimated_total)}</span>
                      </span>
                    )}
                    {b.discount_code && (
                      <span className="font-label text-[0.65rem] px-2 py-1 border" style={{ borderColor: "var(--ss-border)" }}>
                        PROMO <span className="ml-1" style={{ color: "var(--ss-gold)" }}>{b.discount_code}</span>
                      </span>
                    )}
                    {b.gift_card_code && (
                      <span className="font-label text-[0.65rem] px-2 py-1 border" style={{ borderColor: "var(--ss-gold)", color: "var(--ss-gold)" }} data-testid={`booking-gift-tag-${b.id}`}>
                        GIFT {b.gift_card_code} · ${Math.floor(b.gift_credit_applied || 0)}
                      </span>
                    )}
                    {b.gift_card_code && !cardRedeemed && onOpenGiftRedeem && (
                      <button data-testid={`booking-redeem-btn-${b.id}`} onClick={() => onOpenGiftRedeem(b)}
                              className="font-label text-[0.65rem] px-3 py-1 border ml-auto"
                              style={{ borderColor: "var(--ss-rust)", color: "var(--ss-rust)" }}>
                        Confirm & Redeem Gift
                      </button>
                    )}
                    {b.gift_card_code && cardRedeemed && (
                      <span className="font-label text-[0.65rem] px-3 py-1 ml-auto" style={{ color: "var(--ss-muted)" }}>Redeemed</span>
                    )}
                  </div>
                )}

                {b.message && <p className="mt-3 text-sm font-body whitespace-pre-wrap" style={{ color: "var(--ss-cream-dim)" }}>{b.message}</p>}

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="font-label text-[0.65rem]" style={{ color: "var(--ss-cream-dim)" }}>STATUS:</span>
                  {STATUS_FLOW.map((s) => (
                    <button key={s}
                            data-testid={`booking-set-${s}-${b.id}`}
                            onClick={() => changeStatus(b, s)}
                            disabled={b.status === s}
                            className="font-label text-[0.6rem] tracking-widest px-2.5 py-1 border"
                            style={{
                              background: b.status === s ? STATUS_META[s].bg : "transparent",
                              color: b.status === s ? STATUS_META[s].color : "var(--ss-cream-dim)",
                              borderColor: b.status === s ? STATUS_META[s].bg : "var(--ss-border)",
                              cursor: b.status === s ? "default" : "pointer",
                              opacity: b.status === s ? 1 : 0.75,
                            }}>
                      {STATUS_META[s].label}
                    </button>
                  ))}
                  <button data-testid={`booking-manage-${b.id}`} onClick={() => setManageId(b.id)}
                          className="ml-auto font-label text-[0.6rem] tracking-widest px-3 py-1 border"
                          style={{ borderColor: "var(--ss-rust)", background: "var(--ss-rust)", color: "#F5F1E8" }}>
                    MANAGE BOOKING
                  </button>
                  <button data-testid={`booking-delete-${b.id}`} onClick={() => removeBooking(b)}
                          className="font-label text-[0.6rem] tracking-widest px-2 py-1" style={{ color: "var(--ss-muted)" }}>
                    DELETE
                  </button>
                </div>

                <div className="mt-4">
                  <label className="font-label text-[0.65rem]" style={{ color: "var(--ss-cream-dim)" }}>Private tracker notes</label>
                  <textarea data-testid={`booking-notes-${b.id}`}
                            value={draft ?? b.tracker_notes ?? ""}
                            onChange={(e) => setNotesDraft((p) => ({ ...p, [b.id]: e.target.value }))}
                            placeholder="Where you are with this one — quote sent $x, deposit due Fri, etc."
                            rows={2}
                            className="editorial-input mt-1 text-sm" />
                  {notesDirty && (
                    <button data-testid={`booking-notes-save-${b.id}`} onClick={() => saveNotes(b)}
                            className="btn-outline mt-2">Save notes</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === "calendar" && (
        <div data-testid="bookings-calendar">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <button data-testid="cal-prev" onClick={prevMonth} className="btn-outline px-3 py-2"><ChevronLeft size={16} /></button>
              <div className="font-display text-xl sm:text-2xl px-2 sm:px-3">{MONTHS[calMonth.m - 1]} {calMonth.y}</div>
              <button data-testid="cal-next" onClick={nextMonth} className="btn-outline px-3 py-2"><ChevronRight size={16} /></button>
            </div>
            <button data-testid="cal-today" onClick={jumpToday} className="btn-outline">Today</button>
          </div>

          {calLoading && <p style={{ color: "var(--ss-cream-dim)" }}>Loading calendar…</p>}

          {cal && (
            <>
              <div className="grid grid-cols-7 gap-px p-px border [&>*]:min-w-0 overflow-hidden" style={{ borderColor: "var(--ss-border)", background: "var(--ss-border)" }}>
                {WEEKDAYS.map((w) => (
                  <div key={w} className="font-label text-[0.6rem] tracking-widest px-2 py-2 text-center" style={{ background: "var(--ss-bg-2)", color: "var(--ss-cream-dim)" }}>{w}</div>
                ))}
                {Array.from({ length: cal.first_day_weekday }).map((_, i) => (
                  <div key={`blank-${i}`} style={{ background: "var(--ss-bg-2)", minHeight: 90 }} />
                ))}
                {Array.from({ length: cal.days_in_month }).map((_, i) => {
                  const dayNum = i + 1;
                  const dateStr = `${cal.year}-${String(cal.month).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
                  const entries = cal.days[dateStr] || [];
                  const dayBlocks = (cal.blocks && cal.blocks[dateStr]) || [];
                  const doubles = cal.double_bookings[dateStr];
                  const todayIso = cal.today || new Date().toISOString().slice(0, 10);
                  const isToday = dateStr === todayIso;
                  const isPast = dateStr < todayIso;
                  return (
                    <button key={dateStr}
                            data-testid={`cal-day-${dateStr}`}
                            onClick={() => openDay(dateStr)}
                            className="text-left p-2 relative hover:brightness-110 transition-all"
                            style={{ background: "var(--ss-bg-2)", minHeight: 90, cursor: "pointer", border: isToday ? "1px solid var(--ss-rust)" : "none", opacity: isPast ? 0.6 : 1 }}>
                      <div className="flex items-center justify-between">
                        <span className="font-display text-sm" style={{ color: isToday ? "var(--ss-rust)" : "var(--ss-cream)" }}>{dayNum}</span>
                        <div className="flex items-center gap-1">
                          {dayBlocks.length > 0 && (
                            <span data-testid={`cal-block-${dateStr}`} title={`Blocked (${dayBlocks.length})`}>
                              <Ban size={13} color="#8A6B3B" />
                            </span>
                          )}
                          {doubles && (
                            <span data-testid={`cal-double-${dateStr}`} title="Double booking">
                              <AlertTriangle size={13} color="var(--ss-rust)" />
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-1 space-y-0.5">
                        {entries.slice(0, 2).map((e) => (
                          <div key={e.id} className="text-[10px] leading-tight truncate" style={{ color: "var(--ss-cream-dim)" }}>
                            <span style={{ color: STATUS_META[e.status]?.bg !== "transparent" ? STATUS_META[e.status].bg : "var(--ss-cream)" }}>●</span> {e.name}
                          </div>
                        ))}
                        {entries.length > 2 && <div className="text-[10px]" style={{ color: "var(--ss-muted)" }}>+{entries.length - 2} more</div>}
                        {dayBlocks.length > 0 && (
                          <div className="text-[10px] leading-tight truncate font-label" style={{ color: "#8A6B3B" }}>
                            BLOCKED · {dayBlocks.map(b => SERVICE_META[b.resource]?.short || b.resource).join(" ")}
                          </div>
                        )}
                      </div>
                      <div className="mt-1 flex gap-0.5 flex-wrap">
                        {Array.from(new Set(entries.flatMap((e) => e.services))).map((s) => (
                          <span key={s} className="inline-block w-2 h-2 rounded-full" style={{ background: SERVICE_META[s]?.color || "transparent" }} />
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-4 flex flex-wrap items-center gap-4 text-xs" style={{ color: "var(--ss-cream-dim)" }}>
                <span className="font-label text-[0.6rem] tracking-widest">LEGEND</span>
                {Object.entries(SERVICE_META).map(([k, m]) => (
                  <span key={k} className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-2 h-2 rounded-full" style={{ background: m.color }} />
                    {m.label}
                  </span>
                ))}
                <span className="inline-flex items-center gap-1.5">
                  <AlertTriangle size={12} color="var(--ss-rust)" /> Double booking
                </span>
              </div>
            </>
          )}

          {/* Day detail */}
          {dayDetail && cal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
                 style={{ background: "rgba(0,0,0,0.85)" }}
                 onClick={() => setDayDetail(null)}
                 data-testid="cal-day-detail-overlay">
              <div onClick={(e) => e.stopPropagation()}
                   className="w-full max-w-2xl p-6 border max-h-[85vh] overflow-y-auto"
                   style={{ background: "var(--ss-bg-2)", borderColor: "var(--ss-rust)" }}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Manage date</div>
                    <div className="font-display text-3xl mt-1">{dayDetail}</div>
                  </div>
                  <button onClick={() => setDayDetail(null)} data-testid="cal-day-detail-close"><X size={20} /></button>
                </div>

                {cal.double_bookings[dayDetail] && (
                  <div className="mb-4 p-3 border flex items-start gap-2" style={{ borderColor: "var(--ss-rust)", background: "rgba(160,30,42,0.1)" }}>
                    <AlertTriangle size={16} color="var(--ss-rust)" className="mt-0.5" />
                    <div className="text-sm">
                      <div className="font-label text-[0.65rem]" style={{ color: "var(--ss-rust)" }}>DOUBLE BOOKING</div>
                      {Object.entries(cal.double_bookings[dayDetail]).map(([svc, who]) => (
                        <div key={svc} style={{ color: "var(--ss-cream-dim)" }}>
                          <strong style={{ color: "var(--ss-cream)" }}>{SERVICE_META[svc]?.label}</strong> is on {who.length} bookings: {who.map((w) => w.name).join(", ")}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bookings on this day */}
                <div className="font-label text-[0.65rem] mb-2" style={{ color: "var(--ss-cream-dim)" }}>BOOKINGS</div>
                {(cal.days[dayDetail] || []).length === 0 ? (
                  <p className="text-sm italic mb-5" style={{ color: "var(--ss-muted)" }}>No bookings on this date.</p>
                ) : (
                  <div className="space-y-3 mb-6">
                    {(cal.days[dayDetail] || []).map((e) => (
                      <div key={e.id} className="p-3 border" style={{ borderColor: "var(--ss-border)" }} data-testid={`cal-day-entry-${e.id}`}>
                        <div className="flex justify-between items-start gap-3">
                          <div>
                            <div className="font-display text-xl">{e.name}</div>
                            <div className="text-sm" style={{ color: "var(--ss-cream-dim)" }}>{e.event_type} · {e.guest_count} guests · {e.event_location}</div>
                          </div>
                          <Badge status={e.status} />
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {e.services.map((s) => <ServicePill key={s} serviceKey={s} />)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Existing manual blocks on this day */}
                {(cal.blocks && cal.blocks[dayDetail] && cal.blocks[dayDetail].length > 0) && (
                  <div className="mb-6">
                    <div className="font-label text-[0.65rem] mb-2 flex items-center gap-2" style={{ color: "#8A6B3B" }}>
                      <Lock size={11} /> MANUAL BLOCKS
                    </div>
                    <div className="space-y-2">
                      {cal.blocks[dayDetail].map((bl) => {
                        const svc = SERVICE_META[bl.resource];
                        return (
                          <div key={bl.id} className="flex flex-wrap items-center gap-3 p-3 border" style={{ borderColor: "#8A6B3B" }} data-testid={`cal-block-row-${bl.id}`}>
                            <span className="font-label text-[0.65rem] px-2 py-1" style={{ background: `${svc?.color}22`, color: svc?.color, border: `1px solid ${svc?.color}` }}>
                              {svc?.short || bl.resource}
                            </span>
                            <span className="text-sm" style={{ color: "var(--ss-cream)" }}>{svc?.label}</span>
                            {bl.reason && <span className="text-sm italic" style={{ color: "var(--ss-cream-dim)" }}>— {bl.reason}</span>}
                            <button data-testid={`cal-unblock-${bl.id}`} onClick={() => unblockOne(bl.id)}
                                    className="ml-auto font-label text-[0.6rem] tracking-widest px-2 py-1 border" style={{ color: "var(--ss-rust)", borderColor: "var(--ss-rust)" }}>
                              UNBLOCK
                            </button>
                          </div>
                        );
                      })}
                      {cal.blocks[dayDetail].length > 1 && (
                        <button data-testid="cal-unblock-all-day" onClick={() => unblockAllForDay(dayDetail)}
                                className="font-label text-[0.6rem] tracking-widest px-3 py-1 border" style={{ color: "var(--ss-rust)", borderColor: "var(--ss-rust)" }}>
                          UNBLOCK ALL ON THIS DATE
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Inline Block form */}
                <div className="border p-4" style={{ borderColor: "#8A6B3B", background: "rgba(138,107,59,0.08)" }} data-testid="cal-block-form">
                  <div className="font-label text-[0.65rem] mb-3 flex items-center gap-2" style={{ color: "#8A6B3B" }}>
                    <Ban size={11} /> BLOCK A SERVICE ON THIS DATE
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <label className="font-label text-[0.6rem]" style={{ color: "var(--ss-cream-dim)" }}>Service / resource</label>
                      <select data-testid="cal-block-resource"
                              value={dayBlockForm.resource}
                              onChange={(e) => setDayBlockForm({ ...dayBlockForm, resource: e.target.value })}
                              className="editorial-input mt-1">
                        <option value="all">All Services</option>
                        {resources.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="font-label text-[0.6rem]" style={{ color: "var(--ss-cream-dim)" }}>End date (optional, inclusive)</label>
                      <input data-testid="cal-block-end-date" type="date" min={dayDetail}
                             value={dayBlockForm.end_date}
                             onChange={(e) => setDayBlockForm({ ...dayBlockForm, end_date: e.target.value })}
                             className="editorial-input mt-1" />
                    </div>
                    <div>
                      <label className="font-label text-[0.6rem]" style={{ color: "var(--ss-cream-dim)" }}>Private reason</label>
                      <input data-testid="cal-block-reason" type="text"
                             placeholder="Vacation, maintenance, held…"
                             value={dayBlockForm.reason}
                             onChange={(e) => setDayBlockForm({ ...dayBlockForm, reason: e.target.value })}
                             className="editorial-input mt-1" />
                    </div>
                  </div>
                  <div className="flex justify-between mt-4">
                    <button data-testid="cal-block-cancel" onClick={() => setDayDetail(null)} className="btn-outline">Cancel</button>
                    <button data-testid="cal-block-submit" onClick={blockFromDay} className="btn-primary">
                      {dayBlockForm.end_date && dayBlockForm.end_date > dayDetail
                        ? `Block ${dayBlockForm.resource === "all" ? "all services" : SERVICE_META[dayBlockForm.resource]?.label} · ${dayDetail} → ${dayBlockForm.end_date}`
                        : `Block ${dayBlockForm.resource === "all" ? "all services" : SERVICE_META[dayBlockForm.resource]?.label} · ${dayDetail}`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
